import ExportToExcelButton from "@app/components/export-to-excel-button";
import GenerateExportButton from "@app/components/generate-export-button";
import type { XlsxCell, XlsxTable } from "@app/lib/export-xlsx";
import type { PnlCellScope } from "@app/pages/pnl/pnl-cell-panel";
import { environment } from "@app/relay/relay-environment";
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { fetchQuery, graphql, usePreloadedQuery } from "react-relay";
import type { PreloadedQuery } from "react-relay";

import type { diligencePage_Query } from "@app/queries/diligencePage_Query.graphql";
import DiligenceCellPanel from "./diligence-cell-panel";
import QboPnlCellPanel, { type QboPnlCellScope } from "./qbo-pnl-cell-panel";
import RetentionCharts from "./retention-charts";

export const diligenceQuery = graphql`
  query diligencePage_Query {
    diligence {
      currentArr
      arrByYear {
        year
        value
      }
      annualPnl {
        years
        revenue {
          year
          value
        }
        cogs {
          year
          value
        }
        grossProfit {
          year
          value
        }
        grossMargin {
          year
          value
        }
        sAndM {
          year
          value
        }
        rAndD {
          year
          value
        }
        gAndA {
          year
          value
        }
        uncategorized {
          year
          value
        }
        ebitda {
          year
          value
        }
        ebitdaMargin {
          year
          value
        }
        revenueGrowth {
          year
          value
        }
        taxes {
          year
          value
        }
        taxPaid {
          year
          value
        }
        interestAndFees {
          year
          value
        }
        loanProceeds {
          year
          value
        }
        loanPrincipalRepaid {
          year
          value
        }
        distributions {
          year
          value
        }
        afterDistributions {
          year
          value
        }
        debtYearEnd {
          year
          value
        }
        categories {
          name
          pnlLine
          category {
            id
          }
          byYear {
            year
            value
          }
          subcategories {
            name
            category {
              id
            }
            byYear {
              year
              value
            }
          }
        }
      }
      qboPnl {
        connected
        basis
        years
        revenue {
          year
          value
        }
        cogs {
          year
          value
        }
        grossProfit {
          year
          value
        }
        marketing {
          year
          value
        }
        marketingQbo {
          year
          value
        }
        marketingReclass {
          year
          value
        }
        otherOpex {
          year
          value
        }
        netIncome {
          year
          value
        }
        marketingAccounts {
          name
          byYear {
            year
            value
          }
        }
        marketingStakeholders {
          name
          stakeholder {
            id
          }
          total
          byYear {
            year
            value
          }
        }
      }
      retention {
        global {
          band
          nrr
          grr
          logoRetention
          baseCount
          curCount
        }
        bySeat {
          band
          nrr
          grr
          logoRetention
          baseCount
        }
        byTenure {
          band
          nrr
          grr
          logoRetention
          baseCount
        }
      }
      retentionSeries {
        seat {
          ttm {
            band
            points {
              month
              nrr
              grr
              logoChurn
              logoChurnCancellations
            }
          }
          mom {
            band
            points {
              month
              nrr
              grr
              logoChurn
              logoChurnCancellations
            }
          }
        }
        tenure {
          ttm {
            band
            points {
              month
              nrr
              grr
              logoChurn
              logoChurnCancellations
            }
          }
          mom {
            band
            points {
              month
              nrr
              grr
              logoChurn
              logoChurnCancellations
            }
          }
        }
      }
    }
  }
`;

// Relay types nullable scalars as `T | null | undefined`; mirror that exactly so
// these value objects accept the generated query data without a cast.
type Nullable = number | null | undefined;
type YearMetric = { readonly year: number; readonly value: Nullable };
type RetentionRow = {
  readonly band: string;
  readonly nrr: Nullable;
  readonly grr: Nullable;
  readonly logoRetention: Nullable;
  readonly baseCount: number;
  readonly curCount?: number;
};

const usd = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const fmtMoney = (value: number | null | undefined) =>
  value == null ? "—" : usd.format(value);

const fmtPct = (value: number | null | undefined) =>
  value == null ? "—" : `${(value * 100).toFixed(1)}%`;

// A P&L line as year-keyed lookup so the row renders in the header's year order.
const byYear = (points: ReadonlyArray<YearMetric>) => {
  const map = new Map<number, Nullable>();
  points.forEach((p) => map.set(p.year, p.value));
  return map;
};

function StatCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-400">
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold text-gray-900">{value}</div>
      {hint && <div className="mt-1 text-xs text-gray-400">{hint}</div>}
    </div>
  );
}

const cellNum = "px-3 py-2 text-right font-mono tabular-nums text-gray-800";
const cellHead =
  "px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500";

// A P&L row. Every money cell is a button: clicking it opens the transactions
// that sum to it (onCell). A cell with no number is not clickable — there is
// nothing behind a dash.
function MoneyRow({
  label,
  series,
  years,
  bold,
  hint,
  onCell,
}: {
  label: string;
  series: Map<number, Nullable>;
  years: ReadonlyArray<number>;
  bold?: boolean;
  hint?: string;
  onCell: (year: number, value: Nullable) => void;
}) {
  return (
    <tr className={bold ? "border-t border-gray-200 font-semibold" : ""}>
      <td
        className={`px-3 py-2 text-left ${
          bold ? "text-gray-900" : "text-gray-600"
        }`}
        title={hint}
      >
        {label}
      </td>
      {years.map((y) => {
        const value = series.get(y);
        return (
          <td key={y} className={cellNum}>
            {value == null ? (
              "—"
            ) : (
              <button
                type="button"
                onClick={() => onCell(y, value)}
                title={`Show the transactions behind ${label} ${y}`}
                className="rounded px-1 underline decoration-dotted decoration-gray-300 underline-offset-4 hover:bg-blue-50 hover:text-blue-700 hover:decoration-blue-400"
              >
                {fmtMoney(value)}
              </button>
            )}
          </td>
        );
      })}
    </tr>
  );
}

// One row of the annual statement. The same shape /pnl builds for its monthly
// grid, read by year: a label at a depth, a value per column, and — where a
// number is a sum of transactions — the drill that opens them.
type StatementRow = {
  id: string;
  label: string;
  kind: "money" | "percent";
  bold?: boolean;
  indent?: 0 | 1 | 2 | 3;
  memo?: boolean; // a financing cash movement: shown, never part of a subtotal
  hint?: string;
  group?: string; // the pnl_line this row expands, when it is a group header
  expandable?: boolean;
  valueAt: (year: number) => Nullable;
  drill: ((year: number) => void) | null;
};

const indentClass = ["", "pl-6", "pl-12", "pl-[4.5rem]"] as const;

// A statement row. Every money cell that has a number and a drill is a button:
// clicking it opens the transactions that sum to it. A dash is not clickable —
// there is nothing behind it — and a percentage never is: it is two numbers
// divided, not a set of transactions.
function StatementRow({
  row,
  years,
  expanded,
  onToggle,
}: {
  row: StatementRow;
  years: ReadonlyArray<number>;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <tr
      className={`${row.bold ? "border-t border-gray-200 font-semibold" : ""} ${
        row.memo ? "italic text-gray-400" : ""
      }`}
    >
      <td
        className={`px-3 py-2 text-left ${indentClass[row.indent ?? 0]} ${
          row.bold ? "text-gray-900" : "text-gray-600"
        }`}
        title={row.hint}
      >
        <span className="inline-flex items-center gap-1">
          {row.expandable ? (
            <button
              type="button"
              onClick={onToggle}
              className="w-4 shrink-0 text-gray-400 hover:text-gray-700"
              title={expanded ? "Hide its categories" : "Show its categories"}
            >
              {expanded ? "\u25be" : "\u25b8"}
            </button>
          ) : (
            <span className="w-4 shrink-0" />
          )}
          {row.label}
        </span>
      </td>
      {years.map((year) => {
        const value = row.valueAt(year);
        const text =
          row.kind === "percent" ? fmtPct(value) : fmtMoney(value);
        const drillable = row.drill != null && value != null && value !== 0;
        return (
          <td key={year} className={cellNum}>
            {drillable ? (
              <button
                type="button"
                onClick={() => row.drill?.(year)}
                title={`Show the transactions behind ${row.label} ${year}`}
                className="rounded px-1 underline decoration-dotted decoration-gray-300 underline-offset-4 hover:bg-blue-50 hover:text-blue-700 hover:decoration-blue-400"
              >
                {text}
              </button>
            ) : (
              text
            )}
          </td>
        );
      })}
    </tr>
  );
}

function RetentionTable({
  title,
  rows,
}: {
  title: string;
  rows: ReadonlyArray<RetentionRow>;
}) {
  return (
    <div className="overflow-x-auto">
      <h3 className="mb-2 text-sm font-semibold text-gray-700">{title}</h3>
      <table className="min-w-full border border-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              Segment
            </th>
            <th className={cellHead}>NRR</th>
            <th className={cellHead}>GRR</th>
            <th className={cellHead}>Logo</th>
            <th className={cellHead}>Base customers</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.band} className="border-t border-gray-100">
              <td className="px-3 py-2 text-left text-gray-700">{r.band}</td>
              <td className={cellNum}>{fmtPct(r.nrr)}</td>
              <td className={cellNum}>{fmtPct(r.grr)}</td>
              <td className={cellNum}>{fmtPct(r.logoRetention)}</td>
              <td className={cellNum}>{r.baseCount}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function DiligencePage({
  queryRef,
}: {
  queryRef: PreloadedQuery<diligencePage_Query>;
}) {
  const preloaded = usePreloadedQuery(diligenceQuery, queryRef);
  const [data, setData] = useState(preloaded);
  const { annualPnl, qboPnl, retention, arrByYear, currentArr } =
    data.diligence;
  const years = annualPnl.years;
  const qboYears = qboPnl.years;

  // Which cell is open. The two panels answer to different sources, because the
  // tables do: our own statement drills into our own transactions through the
  // editable /pnl panel, while the QuickBooks reconciliation table drills into
  // QuickBooks' own P&L Detail report.
  const [qboCell, setQboCell] = useState<QboPnlCellScope | null>(null);
  const [pnlCell, setPnlCell] = useState<PnlCellScope | null>(null);
  // Which rows are open — line tokens and category keys alike. Collapsed by
  // default: the statement reads as a statement first, and opens where a reader
  // wants the detail.
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());

  const toggle = (line: string) =>
    setExpanded((previous) => {
      const next = new Set(previous);
      if (next.has(line)) next.delete(line);
      else next.add(line);
      return next;
    });

  // Same re-sync rule as /pnl: key on queryRef, not on `preloaded`, so an
  // imperative refresh() is not clobbered by the stale initial snapshot.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setData(preloaded), [queryRef]);

  // A fix made inside a drill re-derives server-side (Pipeline::Delta), which
  // moves the very line it was opened from — refetch so the table reflects it.
  const refresh = () =>
    fetchQuery<diligencePage_Query>(environment, diligenceQuery, {})
      .toPromise()
      .then((fresh) => {
        if (fresh) setData(fresh);
      });

  const openQbo =
    (line: QboPnlCellScope["line"], label: string) =>
    (year: number, value: Nullable) =>
      setQboCell({ line, year, label: `${label} · ${year}`, cellValue: value });

  // A line of our own statement over that calendar year — the very set the number
  // sums. `openStart` drops the lower bound for the debt row: a balance accrues
  // from the beginning, so bounding it to January would show one year of loan
  // activity instead of the balance the cell states.
  const openPnl =
    (line: PnlCellScope["line"], label: string, openStart = false) =>
    (year: number) =>
      setPnlCell({
        line,
        categoryId: null,
        counterpartyId: null,
        from: openStart ? undefined : `${year}-01-01`,
        to: `${year}-12-31`,
        label: `${label} · ${year}`,
      });

  // The S&M line narrowed to one counterparty — the reconciliation table's rows.
  const openMarketingCounterparty = (
    year: number,
    counterpartyId: string,
    label: string
  ) =>
    setPnlCell({
      line: "S_AND_M",
      categoryId: null,
      counterpartyId,
      from: `${year}-01-01`,
      to: `${year}-12-31`,
      label: `${label} · ${year}`,
    });

  // One top-level category's row, drilled by its own category id — the same
  // panel the line above it opens, narrowed to that category.
  const openCategory =
    (line: PnlCellScope["line"], categoryId: string, label: string) => (year: number) =>
    setPnlCell({
      line,
      categoryId,
      counterpartyId: null,
      from: `${year}-01-01`,
      to: `${year}-12-31`,
      label: `${label} · ${year}`,
    });

  const categoriesByLine = new Map<string, typeof annualPnl.categories[number][]>();
  for (const category of annualPnl.categories) {
    const list = categoriesByLine.get(category.pnlLine) ?? [];
    list.push(category);
    categoriesByLine.set(category.pnlLine, list);
  }

  const moneyRow = (
    id: string,
    label: string,
    points: ReadonlyArray<YearMetric>,
    drill: ((year: number) => void) | null,
    opts: { bold?: boolean; indent?: 0 | 1 | 2 | 3; memo?: boolean; hint?: string } = {}
  ): StatementRow => {
    const series = byYear(points);
    return { id, label, kind: "money", ...opts, valueAt: (year) => series.get(year), drill };
  };

  const ratioRow = (
    id: string,
    label: string,
    points: ReadonlyArray<YearMetric>,
    hint?: string
  ): StatementRow => {
    const series = byYear(points);
    return { id, label, kind: "percent", indent: 1, hint, valueAt: (year) => series.get(year), drill: null };
  };

  // A cost line and, as the reader opens them, the two levels of detail beneath
  // it: the top-level categories inside the line (Marketing and Customer success
  // under Sales & marketing, Executive compensation under G&A), then the
  // categories inside those (Content & SEO inside Marketing, Executive salaries
  // inside Executive compensation). It stops there — counterparty grain lives on
  // the P&L tab's month view, and an investor reads a statement, not a ledger.
  const costGroup = (
    line: PnlCellScope["line"],
    label: string,
    points: ReadonlyArray<YearMetric>,
    hint?: string
  ): StatementRow[] => {
    const categories = categoriesByLine.get(line as string) ?? [];
    const header: StatementRow = {
      ...moneyRow(`group:${line}`, label, points, openPnl(line, label), { indent: 1, hint }),
      group: line,
      expandable: categories.length > 0,
    };
    if (!expanded.has(line)) return [header];

    return [
      header,
      ...categories.flatMap((category) => {
        const key = `cat:${line}:${category.name}`;
        const series = byYear(category.byYear);
        const row: StatementRow = {
          id: key,
          label: category.name,
          kind: "money",
          indent: 2,
          hint: `Inside ${label}`,
          group: key,
          expandable: category.subcategories.length > 0,
          valueAt: (year) => series.get(year),
          // The uncategorized bucket is not a category, so there is nothing to
          // drill by — its line drill is the same set of transactions anyway.
          drill: category.category
            ? openCategory(line, category.category.id, category.name)
            : openPnl(line, label),
        };
        if (!expanded.has(key)) return [row];

        return [
          row,
          ...category.subcategories.map((sub): StatementRow => {
            const subSeries = byYear(sub.byYear);
            return {
              id: `${key}:${sub.name}`,
              label: sub.name,
              kind: "money",
              indent: 3,
              hint: `Inside ${category.name}`,
              valueAt: (year) => subSeries.get(year),
              drill: sub.category
                ? openCategory(line, sub.category.id, sub.name)
                : null,
            };
          }),
        ];
      }),
    ];
  };

  const hasFinancing = [
    annualPnl.interestAndFees,
    annualPnl.loanProceeds,
    annualPnl.loanPrincipalRepaid,
  ].some((series) => series.some((point) => (point.value ?? 0) !== 0));

  // The statement, in the order /pnl states it.
  const statementRows: StatementRow[] = [
    moneyRow("revenue", "Revenue", annualPnl.revenue, openPnl("REVENUE", "Revenue"), { bold: true }),
    ...costGroup(
      "COGS",
      "Cost of revenue (COGS)",
      annualPnl.cogs,
      "Counterparties categorized Cost of revenue — Support"
    ),
    moneyRow("gross_profit", "Gross profit", annualPnl.grossProfit, openPnl("GROSS_PROFIT", "Gross profit"), {
      bold: true,
    }),
    ratioRow("gross_margin", "Gross margin", annualPnl.grossMargin),
    ...costGroup(
      "S_AND_M",
      "Sales & marketing",
      annualPnl.sAndM,
      "Marketing, Customer success — the same classification the CAC tab reads"
    ),
    ...costGroup("R_AND_D", "R&D (Engineering)", annualPnl.rAndD),
    ...costGroup("G_AND_A", "G&A", annualPnl.gAndA, "Including Executive compensation"),
    ...costGroup(
      "UNCATEGORIZED",
      "Uncategorized costs",
      annualPnl.uncategorized,
      "Cost whose top-level category has no P&L line set — or that is not categorized at all"
    ),
    moneyRow("ebitda", "EBITDA", annualPnl.ebitda, openPnl("EBITDA", "EBITDA"), { bold: true }),
    ratioRow("ebitda_margin", "EBITDA margin", annualPnl.ebitdaMargin),
    ratioRow(
      "revenue_growth",
      "Revenue growth (YoY)",
      annualPnl.revenueGrowth,
      "Against the prior calendar year; blank in the first year there is nothing to grow from"
    ),
  ];

  // Below the operating statement. None of these is an operating cost; the two
  // loan memos are not costs at all, and the debt row is a balance, not a flow —
  // so none of them is summed into EBITDA above.
  const belowTheLineRows: StatementRow[] = [
    ...costGroup(
      "TAXES",
      "Taxes (categorized)",
      annualPnl.taxes,
      "Counterparties categorized Taxes — accountants, filing fees, anything booked as tax cost"
    ),
    moneyRow(
      "tax_paid",
      "Tax paid, net of refunds",
      annualPnl.taxPaid,
      openPnl("TAX_PAID", "Tax paid, net of refunds"),
      {
        indent: 1,
        hint: "Cash to the government: tax payments less refunds. A different number from the categorized line — these transactions are non-operating, so they never reach the category rollup.",
      }
    ),
    ...(hasFinancing
      ? [
          moneyRow(
            "interest_and_fees",
            "Interest & financing fees",
            annualPnl.interestAndFees,
            openPnl("INTEREST_AND_FEES", "Interest & financing fees"),
            { indent: 1, hint: "A real cost — just not an operating one, which is what EBITDA excludes" }
          ),
          moneyRow(
            "loan_proceeds",
            "Loan proceeds drawn (memo)",
            annualPnl.loanProceeds,
            openPnl("LOAN_PROCEEDS", "Loan proceeds drawn"),
            { indent: 1, memo: true, hint: "Money borrowed is not income — it is a liability taken on" }
          ),
          moneyRow(
            "loan_principal_repaid",
            "Loan principal repaid (memo)",
            annualPnl.loanPrincipalRepaid,
            openPnl("LOAN_PRINCIPAL_REPAID", "Loan principal repaid"),
            { indent: 1, memo: true, hint: "Repaying principal is not an expense — it retires the liability" }
          ),
        ]
      : []),
    moneyRow(
      "distributions",
      "Owner distributions",
      annualPnl.distributions,
      openPnl("DISTRIBUTIONS", "Owner distributions"),
      { indent: 1, hint: "Dividends and draws taken out of the business — never a cost" }
    ),
    moneyRow(
      "after_distributions",
      "After distributions",
      annualPnl.afterDistributions,
      openPnl("AFTER_DISTRIBUTIONS", "After distributions"),
      { bold: true }
    ),
    moneyRow(
      "debt_year_end",
      "Debt at year end",
      annualPnl.debtYearEnd,
      openPnl("DEBT_OUTSTANDING", "Debt at year end", true),
      {
        indent: 1,
        hint: "Outstanding loan debt on the last day of the year — a balance, not a flow. Drills every loan leg from the beginning through that date.",
      }
    ),
  ];

  // The exact grid on screen — the same rows at the same depth, expanded exactly
  // as the reader left them — so the workbook ties out to the page it came from.
  const buildExport = (): XlsxTable => {
    const cell = (row: StatementRow, year: number): XlsxCell => {
      const value = row.valueAt(year) ?? null;
      return row.kind === "percent" ? { v: value, t: "percent" } : value;
    };
    const line = (row: StatementRow): XlsxCell[] => [
      "  ".repeat(row.indent ?? 0) + row.label,
      ...years.map((year) => cell(row, year)),
    ];
    return {
      filename: "diligence-pnl-by-year",
      sheetName: "P&L by year",
      columns: [
        { label: "Line", type: "text" },
        ...years.map((year) => ({ label: String(year), type: "money" as const })),
      ],
      rows: [
        ...statementRows.map(line),
        ["Below the line", ...years.map(() => null)],
        ...belowTheLineRows.map(line),
      ],
      // The caveats have to travel with the numbers: this file leaves the app.
      notes: [
        "Cash basis — our own statement, the same one the P&L tab shows, summed to calendar years.",
        "Nothing below the line is summed into EBITDA. Loan proceeds and principal repaid are memo rows: borrowing is not income and repaying principal is not an expense.",
        "Debt at year end is a balance at that date, not a flow through the year.",
      ],
    };
  };

  return (
    <div className="p-6 space-y-8">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Investor diligence
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Our categorized P&L by year, ARR, and customer retention —
            everything the fund asked for on one page.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <GenerateExportButton
            kind="VC_DATACUBE"
            label="Generate VC datacube"
          />
          <GenerateExportButton kind="BOARD_PACK" label="Generate board pack" />
        </div>
      </div>

      {/* Top-line */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard
          label="ARR (current)"
          value={fmtMoney(currentArr)}
          hint="Ending MRR × 12 (Stripe-derived)"
        />
        {arrByYear.slice(-2).map((point) => (
          <StatCard
            key={point.year}
            label={`ARR — year-end ${point.year}`}
            value={fmtMoney(point.value)}
          />
        ))}
      </div>

      {/* P&L by year — our own statement, the same one /pnl draws */}
      <section className="space-y-3">
        <div className="flex items-start justify-between gap-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Profit &amp; Loss by year
          </h2>
          {/* This table alone, as it stands on screen. The workbook button in the
              page header is a different thing: the whole diligence pack. */}
          {years.length > 0 && (
            <ExportToExcelButton
              build={buildExport}
              label="Export table"
              className="shrink-0 rounded border border-gray-200 px-2 py-1 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40"
            />
          )}
        </div>
        <div>
          <p className="text-xs text-gray-400">
            Our own statement, cash basis — the same rows the P&amp;L tab shows,
            summed to calendar years. Expand a cost line to see the top-level
            categories inside it: Marketing and Customer success under Sales
            &amp; marketing, Executive compensation under G&amp;A. It stops
            there — counterparty grain lives on the P&amp;L tab&apos;s month
            view. A category sits under a line because of the P&amp;L line on
            its top-level node and nothing else, so setting that line on the
            categories page moves it here and on the P&amp;L tab at once.{" "}
            <span className="text-gray-500">
              Click any number to see every transaction behind it.
            </span>
          </p>
        </div>

        {years.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-sm text-gray-500">
            No transactions yet — import a statement and the P&amp;L fills in.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full border border-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Line
                  </th>
                  {years.map((y) => (
                    <th key={y} className={cellHead}>
                      {y}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {statementRows.map((row) => (
                  <StatementRow
                    key={row.id}
                    row={row}
                    years={years}
                    expanded={row.group ? expanded.has(row.group) : false}
                    onToggle={() => row.group && toggle(row.group)}
                  />
                ))}

                {/* Below the operating statement. None of these is an operating
                    cost, the two loan memos are not costs at all, and the debt
                    row is a balance rather than a flow — so none of them is
                    summed into EBITDA above. */}
                <tr className="border-t-2 border-gray-300">
                  <td
                    className="px-3 py-1.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-400"
                    colSpan={years.length + 1}
                  >
                    Below the line
                  </td>
                </tr>
                {belowTheLineRows.map((row) => (
                  <StatementRow
                    key={row.id}
                    row={row}
                    years={years}
                    expanded={row.group ? expanded.has(row.group) : false}
                    onToggle={() => row.group && toggle(row.group)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* QuickBooks' own P&L, kept as a reconciliation reference. It is NOT the
            headline: its COGS and S&M are the very numbers the categorization work
            exists to correct. Collapsed, because an operator reconciling is the
            only reader who wants it. */}
        <details className="rounded-lg border border-gray-200 bg-gray-50/50">
          <summary className="cursor-pointer px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Reconciliation — QuickBooks&apos; own P&amp;L
          </summary>
          <div className="space-y-2 border-t border-gray-200 bg-white p-3">
            <p className="text-xs text-gray-400">
              QuickBooks&apos; own Profit &amp; Loss report —{" "}
              {qboPnl.basis.toLowerCase()} basis, its own summary rows, so Net
              Income ties to the figure in QuickBooks exactly. Its COGS and
              S&amp;M are known to be wrong, which is why the table above does
              not use them; this one is here to reconcile against, not to quote.
              Marketing (S&amp;M) is the one line already replaced by our
              classification.
            </p>

            {!qboPnl.connected ? (
              <div className="rounded border border-dashed border-gray-300 bg-gray-50 p-4 text-center text-sm text-gray-500">
                QuickBooks isn&apos;t synced yet. Connect QBO and run a sync to
                populate this reconciliation.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Line
                      </th>
                      {qboYears.map((y) => (
                        <th key={y} className={cellHead}>
                          {y}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <MoneyRow
                      label="Revenue"
                      series={byYear(qboPnl.revenue)}
                      years={qboYears}
                      bold
                      onCell={openQbo("REVENUE", "QuickBooks Revenue")}
                    />
                    <MoneyRow
                      label="COGS"
                      series={byYear(qboPnl.cogs)}
                      years={qboYears}
                      onCell={openQbo("COGS", "QuickBooks COGS")}
                    />
                    <MoneyRow
                      label="Gross Profit"
                      series={byYear(qboPnl.grossProfit)}
                      years={qboYears}
                      bold
                      onCell={openQbo(
                        "GROSS_PROFIT",
                        "QuickBooks Gross Profit"
                      )}
                    />
                    <MoneyRow
                      label="Marketing (S&M)"
                      series={byYear(qboPnl.marketing)}
                      years={qboYears}
                      onCell={openPnl("S_AND_M", "Sales & marketing")}
                    />
                    <MoneyRow
                      label="Other OpEx"
                      series={byYear(qboPnl.otherOpex)}
                      years={qboYears}
                      onCell={openQbo("OTHER_OPEX", "QuickBooks Other OpEx")}
                    />
                    <MoneyRow
                      label="Net Income"
                      series={byYear(qboPnl.netIncome)}
                      years={qboYears}
                      bold
                      onCell={openQbo("NET_INCOME", "QuickBooks Net Income")}
                    />
                  </tbody>
                </table>

                {qboPnl.marketingAccounts.length > 0 && (
                  <p className="mt-2 text-xs text-gray-400">
                    For reference, QuickBooks books these to selling accounts:{" "}
                    {qboPnl.marketingAccounts.map((a) => a.name).join(", ")}.
                    They count toward Marketing only once their counterparties
                    are categorized as Sales &amp; Marketing (keeping /diligence
                    and /cac in step).
                  </p>
                )}

                {qboPnl.marketingStakeholders.length > 0 && (
                  <div className="mt-4 overflow-x-auto">
                    <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Reclassified into Marketing (by counterparty)
                    </h3>
                    <table className="min-w-full border border-gray-200 text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Counterparty
                          </th>
                          {qboYears.map((y) => (
                            <th key={y} className={cellHead}>
                              {y}
                            </th>
                          ))}
                          <th className={cellHead}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {qboPnl.marketingStakeholders.map((s) => {
                          const byYr = byYear(s.byYear);
                          const counterpartyId = s.stakeholder?.id;
                          return (
                            <tr
                              key={s.name}
                              className="border-t border-gray-100"
                            >
                              <td className="px-3 py-2 text-left text-gray-700">
                                {s.name}
                              </td>
                              {qboYears.map((y) => {
                                const value = byYr.get(y);
                                return (
                                  <td key={y} className={cellNum}>
                                    {value == null || counterpartyId == null ? (
                                      fmtMoney(value)
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          openMarketingCounterparty(
                                            y,
                                            counterpartyId,
                                            s.name
                                          )
                                        }
                                        title={`Show ${s.name}'s transactions in ${y}`}
                                        className="rounded px-1 underline decoration-dotted decoration-gray-300 underline-offset-4 hover:bg-blue-50 hover:text-blue-700 hover:decoration-blue-400"
                                      >
                                        {fmtMoney(value)}
                                      </button>
                                    )}
                                  </td>
                                );
                              })}
                              <td className={`${cellNum} font-semibold`}>
                                {fmtMoney(s.total)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </details>
      </section>
      {/* Retention */}
      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Customer retention
          </h2>
          <p className="text-xs text-gray-400">
            Trailing-12-month cohort retention (Stripe-derived). GRR caps
            expansion at 100%; NRR includes it. Seat count shows an
            &quot;Unknown&quot; bucket for flat-rate plans that bill no per-seat
            quantity.
          </p>
        </div>

        <RetentionTable title="Global" rows={[retention.global]} />
        <RetentionTable title="By seat count" rows={retention.bySeat} />
        <RetentionTable title="By tenure" rows={retention.byTenure} />

        <div className="pt-2">
          <h3 className="mb-1 text-sm font-semibold text-gray-700">
            Monthly trend
          </h3>
          <p className="mb-3 text-xs text-gray-400">
            NRR, GRR, and both logo-churn measures per month over the last 3
            years — one line per bucket plus the overall book. Toggle each
            dimension between a trailing-12-month and a month-over-month basis.
            Quote <strong>Logo churn (cancellations)</strong>: it counts
            customers who actually left. The cohort measure beside it counts a
            customer whose invoices gapped as churned even if they came back, so
            the space between the two is billing-timing noise rather than
            leavers.
          </p>
          <RetentionCharts series={data.diligence.retentionSeries} />
        </div>

        <div className="pt-4">
          <h3 className="mb-1 text-sm font-semibold text-gray-700">
            The numbers behind the charts
          </h3>
          <p className="mb-3 text-xs text-gray-400">
            Every metric as a row and every month of company history as a
            column, on both bases and across every seat and tenure band. It is
            generated in the background rather than rendered here — the matrix
            is far too large to build inside a page load.
          </p>
          <Link
            to="/diligence/report"
            className="inline-block rounded border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900"
          >
            Open the KPI report →
          </Link>
        </div>
      </section>

      {qboCell && (
        <QboPnlCellPanel scope={qboCell} onClose={() => setQboCell(null)} />
      )}
      {pnlCell && (
        <DiligenceCellPanel
          scope={pnlCell}
          onClose={() => setPnlCell(null)}
          onChanged={() => refresh()}
        />
      )}
    </div>
  );
}
